/**
 * Assessments feature hooks — React Query wrappers.
 */
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { assessmentsApi } from './api'
import type { CreateAssessmentRequest, UpdateAssessmentRequest, SaveAnswerRequest } from './types'
import type { PagedRequest } from '@/shared/types'

// ── Queries ───────────────────────────────────────────────────────────

export function useAssessments(params?: PagedRequest) {
    return useQuery({
        queryKey: ['assessments', params],
        queryFn: async () => {
            const res = await assessmentsApi.list(params)
            return res.data
        },
        placeholderData: keepPreviousData,
    })
}

export function useAssessment(id: string) {
    return useQuery({
        queryKey: ['assessments', id],
        queryFn: async () => {
            const res = await assessmentsApi.get(id)
            return res.data
        },
        enabled: !!id,
    })
}

export function useAssessmentAnswers(id: string, params?: PagedRequest) {
    return useQuery({
        queryKey: ['assessments', id, 'answers', params],
        queryFn: async () => {
            const res = await assessmentsApi.answers(id, params)
            return res.data
        },
        enabled: !!id,
        placeholderData: keepPreviousData,
    })
}

export function useAssessmentProgress(id: string) {
    return useQuery({
        queryKey: ['assessments', id, 'progress'],
        queryFn: async () => {
            const res = await assessmentsApi.progress(id)
            return res.data
        },
        enabled: !!id,
    })
}

export function useAttachments(params: { page?: number, per_page?: number, org_id?: string, assessment_id?: string, question_id?: string, search?: string } = {}) {
    return useQuery({
        queryKey: ['attachments', params],
        queryFn: async () => {
            const res = await assessmentsApi.listAttachments(params)
            return res.data
        },
        placeholderData: keepPreviousData,
    })
}


// ── Mutations ─────────────────────────────────────────────────────────

export function useCreateAssessment() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (data: CreateAssessmentRequest) => assessmentsApi.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['assessments'] })
        },
    })
}

export function useUpdateAssessment() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: UpdateAssessmentRequest }) =>
            assessmentsApi.update(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['assessments'] })
            queryClient.invalidateQueries({ queryKey: ['stats', 'maturity-overview'] })
        },
    })
}

export function useSaveAnswer() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: ({ assessmentId, data, file }: { assessmentId: string; data: SaveAnswerRequest; file?: File }) =>
            assessmentsApi.saveAnswer(assessmentId, data, file),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['assessments', variables.assessmentId, 'answers'] })
            queryClient.invalidateQueries({ queryKey: ['assessments', variables.assessmentId, 'progress'] })
        },
    })
}


export function useServeFile() {
    return useMutation({
        mutationFn: ({ assessmentId, questionId }: { assessmentId: string; questionId: string }) =>
            assessmentsApi.serveFile(assessmentId, questionId),
    })
}

export function usePreviewFile() {
    return useMutation({
        mutationFn: ({ assessmentId, questionId }: { assessmentId: string; questionId: string }) =>
            assessmentsApi.previewFile(assessmentId, questionId),
    })
}

export function useDeleteAssessment() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => assessmentsApi.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['assessments'] })
        },
    })
}
